import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, WifiOff } from 'lucide-react';
import './OfflineScreen.css';

// Full-screen notice while the browser has no connection. It clears by itself the moment the
// connection returns; "Try again" re-checks on demand.
const OfflineScreen = () => {
    const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
    const [checking, setChecking] = useState(false);
    const [stillOffline, setStillOffline] = useState(false);

    useEffect(() => {
        const goOnline = () => { setOnline(true); setStillOffline(false); };
        const goOffline = () => setOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
    }, []);

    const retry = useCallback(async () => {
        setChecking(true);
        setStillOffline(false);
        try {
            await fetch(`${window.location.origin}/favicon.ico?ping=${Date.now()}`, { cache: 'no-store' });
            setOnline(true);
        } catch (error) {
            setStillOffline(true);
        } finally {
            setChecking(false);
        }
    }, []);

    if (online) return null;

    return (
        <div className="offline-screen" role="alertdialog" aria-modal="true" aria-labelledby="offline-title">
            <span className="offline-bubble offline-bubble--a" aria-hidden="true" />
            <span className="offline-bubble offline-bubble--b" aria-hidden="true" />
            <span className="offline-bubble offline-bubble--c" aria-hidden="true" />

            <div className="offline-card">
                <div className="offline-art" aria-hidden="true">
                    <svg viewBox="0 0 200 150" width="190" height="143">
                        <defs>
                            <linearGradient id="off-plug" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0" stopColor="var(--primary)" /><stop offset="1" stopColor="var(--primary-dark)" />
                            </linearGradient>
                        </defs>
                        <rect x="22" y="22" width="78" height="78" rx="18" fill="color-mix(in srgb, var(--primary) 9%, #fff)" stroke="color-mix(in srgb, var(--primary) 24%, #fff)" />
                        <circle cx="48" cy="52" r="5" fill="var(--primary)" /><circle cx="74" cy="52" r="5" fill="var(--primary)" />
                        <path d="M46 72c6 7 22 7 28 0" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" fill="none" />
                        <path d="M100 118c-22 0-18-26-36-26" stroke="var(--primary-light)" strokeWidth="4" strokeLinecap="round" fill="none" strokeDasharray="1 9" />
                        <g transform="translate(112 48)">
                            <rect x="0" y="12" width="60" height="44" rx="12" fill="url(#off-plug)" />
                            <rect x="14" y="0" width="8" height="18" rx="3" fill="var(--primary-light)" />
                            <rect x="38" y="0" width="8" height="18" rx="3" fill="var(--primary-light)" />
                            <path d="M30 56v22c0 8-9 12-18 12" stroke="var(--primary)" strokeWidth="5" strokeLinecap="round" fill="none" />
                            <path d="M20 30l12 10-8 6 10 8" stroke="var(--c-f3d27a, #f3d27a)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </g>
                    </svg>
                    <span className="offline-art__badge"><WifiOff size={18} /></span>
                </div>

                <h2 id="offline-title">No internet connection!</h2>
                <p>Your connection seems to be down. Please check your Wi-Fi or mobile data, then try again to continue with Gettimelam Matrimony.</p>
                {stillOffline && <p className="offline-still" role="status">Still offline — please check your connection.</p>}

                <button type="button" className="offline-retry" onClick={retry} disabled={checking}>
                    {checking ? <><Loader2 size={17} className="offline-spin" /> Checking…</> : <><RefreshCw size={17} /> Try again</>}
                </button>
            </div>
        </div>
    );
};

export default OfflineScreen;
